<template>
  <div class="login-page">
    <UiContainer class="login-page__container">
      <div class="login-page__card-wrap">
        <UiCard class="login-page__card">
          <h1 class="login-page__title">Вход в систему</h1>
          <p v-if="showRedirectNotice" class="login-page__hint login-page__hint--info">
            Войдите, чтобы продолжить.
          </p>
          <p v-if="showPasswordResetNotice" class="login-page__hint login-page__hint--success">
            Пароль изменён. Войдите с новым паролем.
          </p>
          <p v-if="showEmailSentNotice" class="login-page__hint login-page__hint--success">
            На {{ email }} отправлено письмо для подтверждения регистрации.
          </p>
          <form class="login-page__form" @submit.prevent="login">
            <div class="login-page__field">
              <label class="login-page__label" for="login-email">Email</label>
              <UiInput
                id="login-email"
                v-model="email"
                type="email"
                placeholder="email@example.com"
                autocomplete="email"
                required
                :readonly="requiresTwoFactor"
              />
            </div>
            <div v-if="!requiresTwoFactor" class="login-page__field">
              <label class="login-page__label" for="login-password">Пароль</label>
              <UiInput
                id="login-password"
                v-model="password"
                type="password"
                placeholder="••••••••"
                autocomplete="current-password"
                required
              />
            </div>
            <div v-else class="login-page__two-factor">
              <p class="login-page__hint">
                Код подтверждения отправлен на {{ challengeEmail || email }}.
                <span v-if="challengeExpiresAt"
                  >Действует до {{ formatTime(challengeExpiresAt) }}.</span
                >
              </p>
              <div class="login-page__field">
                <label class="login-page__label" for="login-code">Код из письма</label>
                <UiInput
                  id="login-code"
                  v-model="twoFactorCode"
                  inputmode="numeric"
                  placeholder="123456"
                  maxlength="6"
                  autocomplete="one-time-code"
                  required
                />
              </div>
            </div>
            <UiButton type="submit" block :loading="loading">
              {{ requiresTwoFactor ? 'Подтвердить вход' : 'Войти' }}
            </UiButton>
          </form>
          <UiButton
            v-if="requiresTwoFactor"
            variant="secondary"
            block
            class="login-page__reset"
            @click="resetTwoFactor"
          >
            Ввести другой логин
          </UiButton>
          <UiButton
            v-if="showResendVerification"
            variant="secondary"
            block
            class="login-page__reset"
            :loading="loading"
            @click="resendVerification"
          >
            Отправить письмо повторно
          </UiButton>
          <p v-if="!requiresTwoFactor" class="login-page__footer login-page__footer--tight">
            <router-link to="/forgot-password" class="login-page__link">Забыли пароль?</router-link>
          </p>
          <p class="login-page__footer">
            Нет аккаунта?
            <router-link to="/register" class="login-page__link">Зарегистрироваться</router-link>
          </p>
        </UiCard>
      </div>
    </UiContainer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthSession } from '@/modules/auth/public'
import { useAppNotifications } from '@/modules/common/public'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiInput from '@/shared/ui/UiInput.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthSession()
const notificationStore = useAppNotifications()

const email = ref('')
const password = ref('')
const twoFactorCode = ref('')
const challengeToken = ref<string | null>(null)
const challengeExpiresAt = ref<string | null>(null)
const challengeEmail = ref<string | null>(null)
const loading = ref(false)
const requiresTwoFactor = computed(() => challengeToken.value !== null)
const showEmailSentNotice = ref(false)
const showResendVerification = ref(false)
const showPasswordResetNotice = ref(false)

const redirectTarget = computed(() => {
  const target = route.query['redirect']
  if (typeof target !== 'string') return null
  // Только внутренние пути: '//host' и '/\\host' браузер трактует как внешний адрес
  if (!target.startsWith('/') || target.startsWith('//') || target.startsWith('/\\')) return null
  if (target === '/login' || target.startsWith('/login?')) return null
  return target
})
const showRedirectNotice = computed(() => redirectTarget.value !== null)

onMounted(() => {
  if (route.query['emailConfirmationSent'] === '1') {
    showEmailSentNotice.value = true
  }
  if (route.query['passwordReset'] === '1') {
    showPasswordResetNotice.value = true
  }

  const queryEmail = route.query['email']
  if (typeof queryEmail === 'string' && queryEmail.trim().length > 0) {
    email.value = queryEmail.trim()
  }
})

function resetTwoFactor() {
  twoFactorCode.value = ''
  challengeToken.value = null
  challengeExpiresAt.value = null
  challengeEmail.value = null
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

async function login(event?: Event) {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }

  if (!requiresTwoFactor.value && (!email.value || !password.value)) {
    notificationStore.showError('Заполните все поля')
    return
  }
  if (requiresTwoFactor.value && twoFactorCode.value.trim().length !== 6) {
    notificationStore.showError('Введите 6-значный код из письма')
    return
  }

  loading.value = true
  try {
    showResendVerification.value = false

    const result =
      requiresTwoFactor.value && challengeToken.value
        ? await authStore.verifyTwoFactor({
            challengeToken: challengeToken.value,
            code: twoFactorCode.value.trim(),
          })
        : await authStore.login({
            email: email.value,
            password: password.value,
          })

    if (result.success) {
      notificationStore.showSuccess('Вход выполнен успешно')
      resetTwoFactor()
      router.push(redirectTarget.value ?? '/')
    } else if (result.requiresTwoFactor) {
      challengeToken.value = result.challengeToken ?? null
      challengeExpiresAt.value = result.challengeExpiresAt ?? null
      challengeEmail.value = result.challengeEmail ?? email.value
      twoFactorCode.value = ''
      notificationStore.showSuccess('Код подтверждения отправлен на email')
    } else {
      const errorMessage = result.error || 'Ошибка входа'
      showResendVerification.value = errorMessage.toLowerCase().includes('подтвердите email')
      notificationStore.showError(result.error || 'Ошибка входа')
    }
  } catch {
    notificationStore.showError('Произошла неожиданная ошибка')
  } finally {
    loading.value = false
  }
}

async function resendVerification() {
  const normalizedEmail = email.value.trim()
  if (!normalizedEmail) {
    notificationStore.showError('Введите email')
    return
  }

  loading.value = true
  try {
    const result = await authStore.resendEmailVerification(normalizedEmail)
    if (result.success) {
      notificationStore.showSuccess(result.message || 'Письмо для подтверждения отправлено')
    } else {
      notificationStore.showError(result.error || 'Не удалось отправить письмо')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-page {
  min-height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
}

.login-page__container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 32px;
  padding-bottom: 32px;
}

.login-page__card-wrap {
  width: 100%;
  max-width: 400px;
}

.login-page__card {
  padding: 24px;
}

.login-page__title {
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
}

.login-page__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.login-page__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.login-page__label {
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.login-page__footer {
  margin: 24px 0 0;
  text-align: center;
  color: #586578;
  font-size: 14px;
}

.login-page__two-factor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.login-page__hint {
  margin: 0;
  font-size: 14px;
  color: #586578;
}

.login-page__hint--success {
  margin-bottom: 12px;
  color: #047857;
}

.login-page__footer--tight {
  margin-top: 16px;
}

.login-page__hint--info {
  margin-bottom: 12px;
}

.login-page__link {
  color: #0f172a;
  font-weight: 500;
  text-decoration: none;
}

.login-page__link:hover {
  text-decoration: underline;
}

.login-page__reset {
  margin-top: 12px;
}
</style>
